import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';

import { useAuth, useDictionary, usePage } from '@/src/app/hooks';
import {
    getSearchIndex,
    getSearchableGrades,
    searchResults,
    MAX_SEARCH_RESULTS,
    type SearchIndex,
    type SearchResult,
} from '@/src/services/resourceSearch';
import { Constants, getDisplayGrade } from '@/src/utils';
import { SearchIcon } from './SVGIcons';
import Loading from './Loading';
import './css/ResourceSearch.css';

interface ResourceSearchModalProps {
    open: boolean;
    onClose: () => void;
}

// Splits a name on every query token so matches can be bolded in the result row.
function highlight(name: string, tokens: string[]): React.ReactNode {
    if (tokens.length === 0) return name;

    const lower = name.toLowerCase();
    const ranges: [number, number][] = [];

    for (const token of tokens) {
        let from = lower.indexOf(token);
        while (from !== -1) {
            ranges.push([from, from + token.length]);
            from = lower.indexOf(token, from + token.length);
        }
    }
    if (ranges.length === 0) return name;

    ranges.sort((a, b) => a[0] - b[0]);

    // Merge overlapping ranges so nested <mark> elements never happen.
    const merged: [number, number][] = [];
    for (const range of ranges) {
        const last = merged[merged.length - 1];
        if (last && range[0] <= last[1]) {
            last[1] = Math.max(last[1], range[1]);
        } else {
            merged.push([...range]);
        }
    }

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    merged.forEach(([start, end], index) => {
        if (start > cursor) parts.push(name.slice(cursor, start));
        parts.push(<mark key={`match-${index}`}>{name.slice(start, end)}</mark>);
        cursor = end;
    });
    if (cursor < name.length) parts.push(name.slice(cursor));

    return parts;
}

const ResourceSearchModal: React.FC<ResourceSearchModalProps> = ({ open, onClose }) => {
    const { translate } = useDictionary();
    const { user } = useAuth();
    const { pageLanguage } = usePage();

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [index, setIndex] = useState<SearchIndex | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const grades = useMemo(() => getSearchableGrades(user?.allowedGrades), [user?.allowedGrades]);

    // Build the index the first time the dialog is opened, and again if the
    // language or the teacher's grades change. getSearchIndex caches per combination.
    useEffect(() => {
        if (!open || grades.length === 0) return;

        let cancelled = false;
        setLoading(true);
        setError('');

        getSearchIndex(grades, pageLanguage)
            .then((loaded) => {
                if (cancelled) return;
                setIndex(loaded);
            })
            .catch(() => {
                if (cancelled) return;
                setError(translate('Files could not be loaded', 'Files could not be loaded.'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, grades, pageLanguage]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), Constants.SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    // Start each visit with an empty box rather than a stale query.
    useEffect(() => {
        if (!open) {
            setQuery('');
            setDebouncedQuery('');
            setActiveIndex(0);
        }
    }, [open]);

    const tokens = useMemo(
        () => debouncedQuery.toLowerCase().split(/\s+/).filter(Boolean),
        [debouncedQuery]
    );

    const matches = useMemo(
        () => (index ? searchResults(index.results, debouncedQuery) : []),
        [index, debouncedQuery]
    );

    useEffect(() => {
        setActiveIndex(0);
    }, [debouncedQuery]);

    const openResult = useCallback((result: SearchResult) => {
        window.open(result.link, '_blank', 'noopener,noreferrer');
    }, []);

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (matches.length === 0) return;

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((current) => (current + 1) % matches.length);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((current) => (current - 1 + matches.length) % matches.length);
            } else if (event.key === 'Enter') {
                event.preventDefault();
                const result = matches[activeIndex];
                if (result) openResult(result);
            }
        },
        [matches, activeIndex, openResult]
    );

    // Keep the keyboard-selected row inside the scroll area.
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const active = list.children[activeIndex] as HTMLElement | undefined;
        active?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const renderBody = () => {
        if (loading && !index) {
            return (
                <div className="resource-search-status">
                    <Loading message={translate('Loading files', 'Loading files...')} size="sm" />
                </div>
            );
        }

        if (error) {
            return (
                <div className="resource-search-status" role="alert">
                    {error}
                </div>
            );
        }

        if (!debouncedQuery.trim()) {
            return (
                <div className="resource-search-status">
                    {translate(
                        'Type a name, unit, or lesson to search',
                        'Type a name, unit, or lesson to search.'
                    )}
                </div>
            );
        }

        if (matches.length === 0) {
            return (
                <div className="resource-search-status">
                    {translate('No files found', 'No files found')}
                    {' — '}
                    <span className="resource-search-query">{debouncedQuery}</span>
                </div>
            );
        }

        return (
            <>
                <div className="resource-search-count">
                    {matches.length >= MAX_SEARCH_RESULTS
                        ? `${translate('Showing the first', 'Showing the first')} ${MAX_SEARCH_RESULTS}`
                        : `${matches.length} ${
                              matches.length === 1
                                  ? translate('file', 'file')
                                  : translate('files', 'files')
                          }`}
                </div>
                <ul
                    id="resource-search-results"
                    className="resource-search-results"
                    role="listbox"
                    ref={listRef}
                >
                    {matches.map((result, position) => (
                        <li
                            key={result.key}
                            id={`resource-search-option-${position}`}
                            role="option"
                            aria-selected={position === activeIndex}
                            className={`resource-search-result${
                                position === activeIndex ? ' active' : ''
                            }`}
                            onMouseEnter={() => setActiveIndex(position)}
                        >
                            <a
                                href={result.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => onClose()}
                            >
                                <span className="resource-search-name">
                                    {highlight(result.name, tokens)}
                                </span>
                                {result.trail.length > 0 && (
                                    <span className="resource-search-trail">
                                        {highlight(result.trail.join(' › '), tokens)}
                                    </span>
                                )}
                                <span className="resource-search-meta">
                                    {result.grade && (
                                        <span className="resource-search-badge">
                                            {translate('Grade')} {getDisplayGrade(result.grade)}
                                        </span>
                                    )}
                                    <span className="resource-search-badge source">
                                        {translate(result.source, result.source)}
                                    </span>
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="resource-search-title"
            slotProps={{
                paper: { className: 'resource-search-paper' },
            }}
        >
            <div className="resource-search">
                <div className="resource-search-header">
                    <h2
                        id="resource-search-title"
                        className="resource-search-title pd-primary text"
                    >
                        {translate('Search Files', 'Search Files')}
                    </h2>
                    <button
                        type="button"
                        className="resource-search-close"
                        aria-label={translate('Close', 'Close')}
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="resource-search-field">
                    <SearchIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
                    <input
                        ref={inputRef}
                        type="search"
                        autoFocus
                        className="resource-search-input"
                        placeholder={translate(
                            'Search by name, unit, or lesson',
                            'Search by name, unit, or lesson'
                        )}
                        aria-label={translate(
                            'Search by name, unit, or lesson',
                            'Search by name, unit, or lesson'
                        )}
                        aria-controls="resource-search-results"
                        aria-activedescendant={
                            matches.length > 0 ? `resource-search-option-${activeIndex}` : undefined
                        }
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={onKeyDown}
                    />
                </div>

                {index && index.failedSources.length > 0 && (
                    <div className="resource-search-warning" role="status">
                        {translate(
                            'Some files could not be loaded',
                            'Some files could not be loaded'
                        )}
                        {`: ${index.failedSources.join(', ')}`}
                    </div>
                )}

                {renderBody()}
            </div>
        </Dialog>
    );
};

export default ResourceSearchModal;
